import React, { useState, useEffect } from "react";
import { StyledForm } from "lib/styles/general";
import { CTAButton } from "lib/components/CTAButton";
import { CheckboxButton } from "lib/components/Checkbox";
import { KeyValue } from "lib/components/KeyValue";
import { Input } from "lib/components/Input";
import { Select } from "lib/components/Select";
import { fetchData, useFetch, Error } from "lib/helpers/fetchData";
import { Spinner } from "lib/components/Spinner";
import { getApiUrl } from "lib/helpers/getApiUrl";
import { ErrorModal } from "lib/components/Modal";

const MINUTE_IN_MS = 60 * 1000;
const THIRTY_DAYS_IN_MINUTES = 30 * 24 * 60;

const getJiraInstances = async () => {
  return await fetchData(`${getApiUrl("JIRA_MANAGEMENT_API")}/instances`);
};

const getCustomerConfig = async ({ customerId }) => {
  try {
    const existingCustomer = await fetchData(`${getApiUrl("TICKET_SERVICE")}/customers/${customerId}`);
    return existingCustomer;
  } catch (err) {
    if (err.statusCode === 404) {
      return undefined;
    }

    throw err;
  }
};

const loadDefaultConfigs = async () => fetchData(`${getApiUrl("TICKET_SERVICE")}/defaultSlaConfigurations`);

const BREACH_FIELDS = [
  { key: "p0Breach", defaultKey: "p0BreachMs", label: "P0 Breach Time (min)" },
  { key: "p1Breach", defaultKey: "p1BreachMs", label: "P1 Breach Time (min)" },
  { key: "p2Breach", defaultKey: "p2BreachMs", label: "P2 Breach Time (min)" },
  { key: "p3Breach", defaultKey: "p3BreachMs", label: "P3 Breach Time (min)" },
  { key: "phishBreach", defaultKey: "phishBreachMs", label: "Phish Breach Time (min)" },
];

const INTEGER_PATTERN = /^\d+$/;

const buildFormState = (data) => {
  const sla = data ?? {};

  return {
    newSlaEnabled: !!sla.newSlaEnabled,
    customSlaEnabled: !!sla.customSlaEnabled,
    defaultSlaConfigurationId: sla.defaultSlaConfigurationId ?? "",
    ...Object.fromEntries(BREACH_FIELDS.map(({ key }) => [
      key,
      sla[key] == null ? "" : String(Math.max(1, Math.floor(sla[key] / MINUTE_IN_MS))),
    ])),
  };
};

const buildOriginalBreachValues = (data) =>
  Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, data?.[key]]));

const FieldError = ({ error, margin = "-0.75rem 0 10px" }) =>
  error ? <p style={{ color: "rgba(255, 0, 0, 0.8)", fontSize: "1.2rem", margin }}>{error}</p> : null;

export const Sla = ({ data, reload }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { data: customerData, isLoading: customerDataLoading, error: customerDataError } = useFetch(getCustomerConfig, { customerId: data.id });
  const [formState, setFormState] = useState(buildFormState(customerData));
  const [savedState, setSavedState] = useState(buildFormState(customerData));
  const [originalBreachValues, setOriginalBreachValues] = useState(buildOriginalBreachValues(customerData));
  const [changedBreachFields, setChangedBreachFields] = useState({});

  useEffect(() => {
    if (customerData !== undefined) {
      setFormState(buildFormState(customerData));
      setSavedState(buildFormState(customerData));
      setOriginalBreachValues(buildOriginalBreachValues(customerData));
      setChangedBreachFields({});
    }
  }, [customerData]);

  const { data: defaultConfigs } = useFetch(loadDefaultConfigs);
  const configs = defaultConfigs ?? [];

  const validate = () => {
    const newErrors = {};
    if (formState.newSlaEnabled) {

      if (formState.customSlaEnabled) {
        BREACH_FIELDS.forEach(({ key }) => {
          if (!formState[key]) {
            newErrors[key] = "Required";
          } else if (!INTEGER_PATTERN.test(formState[key])) {
            newErrors[key] = "Must be a whole number";
          } else {
            const value = Number.parseInt(formState[key], 10);
            if (value < 1 || value > THIRTY_DAYS_IN_MINUTES) {
              newErrors[key] = `Must be in range 1 - ${THIRTY_DAYS_IN_MINUTES}`;
            }
          }
        });
      } else if (!formState.defaultSlaConfigurationId) {
        newErrors.defaultSlaConfigurationId = "Default configuration is required";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const formStateToPostData = async () => {
    const jiraInstances = await getJiraInstances();
    const instance = jiraInstances.find((i) => i.id === data.connectors.jira.instanceId);

    const baseData = {
      customerId: data.id,
      customerName: data.name,
      jiraInstanceName: instance.name,
      jiraInstanceDomain: instance.domain,
      newSlaEnabled: formState.newSlaEnabled,
      customSlaEnabled: formState.customSlaEnabled,
    };

    const selectedDefaultConfig = configs.find((config) => config.id === formState.defaultSlaConfigurationId);
    const newSlaData = formState.customSlaEnabled
      ? Object.fromEntries(BREACH_FIELDS.map(({ key }) => [
          key,
          changedBreachFields[key]
            ? Number.parseInt(formState[key], 10) * MINUTE_IN_MS
            : originalBreachValues[key],
        ]))
      : {
          defaultSlaConfigurationId: formState.defaultSlaConfigurationId,
          ...Object.fromEntries(BREACH_FIELDS.map(({ key, defaultKey }) => [key, Number.parseInt(selectedDefaultConfig?.[defaultKey], 10)])),
        };

    const customData = formState.newSlaEnabled ? newSlaData : {};

    return { ...baseData, ...customData };
  }

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);

    try {
      const postData = await formStateToPostData();
      await fetchData(`${getApiUrl("TICKET_SERVICE")}/customers/update-sla-configuration?group=${data.groupName}`, JSON.stringify(postData), "POST");
      setSavedState(formState);
      setOriginalBreachValues(Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, postData[key]])));
      setChangedBreachFields({});
      setIsEditMode(false);
      reload?.();
    } catch (err) {
      setErrors({ form: "An error occurred while saving. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormState(savedState);
    setChangedBreachFields({});
    setErrors({});
    setIsEditMode(false);
  };

  const toggleNewSla = (checked) => {
    setFormState((prev) => ({
      ...prev,
      newSlaEnabled: checked,
      customSlaEnabled: checked ? prev.customSlaEnabled : false,
    }));
    if (!checked) setErrors({});
  };

  const toggleCustomSla = (checked) => {
    setFormState((prev) => ({
      ...prev,
      customSlaEnabled: checked,
      defaultSlaConfigurationId: checked ? "" : prev.defaultSlaConfigurationId,
      ...Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, checked ? prev[key] : ""])),
    }));
    setErrors({});
  };

  if (customerDataLoading) return <Spinner />;
  if (customerDataError) return <Error message={customerDataError.message ?? "Unable to retrieve customer configuration"} />;

  return (
    <div style={{ position: "relative" }}>
      {!isEditMode && (
        <CTAButton
          style={{ position: "absolute", right: 0, top: 0, zIndex: 99 }}
          onClick={() => setIsEditMode(true)}
        >
          Edit
        </CTAButton>
      )}
      <StyledForm>
        <section style={{ paddingTop: 0 }}>
          {!isEditMode ? (
            <>
              <KeyValue
                text="New SLA Functionality"
                value={formState.newSlaEnabled ? "Enabled" : "Disabled"}
              />
              {formState.newSlaEnabled && (
                <>
                  <KeyValue
                    text="Custom SLA Configuration"
                    value={formState.customSlaEnabled ? "Enabled" : "Disabled"}
                  />
                  {formState.customSlaEnabled ? (
                    BREACH_FIELDS.map(({ key, label }) => (
                      <KeyValue key={key} text={label} value={formState[key] || "Not set"} />
                    ))
                  ) : (
                    <KeyValue
                      text="Default Configuration"
                      value={configs.find((c) => c.id === formState.defaultSlaConfigurationId)?.name || "Not set"}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <KeyValue
                text="New SLA Functionality"
                wrap={false}
                value={
                  <CheckboxButton
                    key={String(formState.newSlaEnabled)}
                    name="newSlaEnabled"
                    subtle
                    showWhenNull
                    label="Enable New SLA Functionality?"
                    defaultChecked={formState.newSlaEnabled}
                    onChange={(e) => toggleNewSla(e.target.checked)}
                  />
                }
              />
              {formState.newSlaEnabled && (
                <>
                  <KeyValue
                    text="Custom SLA Configuration"
                    wrap={false}
                    value={
                      <CheckboxButton
                        key={String(formState.customSlaEnabled)}
                        name="customSlaEnabled"
                        subtle
                        showWhenNull
                        label="Enable Custom SLA Configuration?"
                        defaultChecked={formState.customSlaEnabled}
                        onChange={(e) => toggleCustomSla(e.target.checked)}
                      />
                    }
                  />
                  <Input
                    label="Default Configuration"
                    onChange={(e) => {
                      setErrors((prev) => ({ ...prev, defaultSlaConfigurationId: null }));
                      setFormState((prev) => ({ ...prev, defaultSlaConfigurationId: e.target.value }));
                    }}
                  >
                    <Select
                      name="defaultSlaConfigurationId"
                      style={{ margin: "0.3rem 0 1rem", width: "100%" }}
                      value={formState.defaultSlaConfigurationId}
                      disabled={formState.customSlaEnabled}
                    >
                      <option value="">Select Default Configuration</option>
                      {configs.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.name}
                        </option>
                      ))}
                    </Select>
                  </Input>
                  {!formState.customSlaEnabled && <FieldError error={errors.defaultSlaConfigurationId} />}
                  {formState.customSlaEnabled &&
                    BREACH_FIELDS.map(({ key, label }) => (
                      <Input
                        key={key}
                        label={label}
                        type="text"
                        inputMode="numeric"
                        value={formState[key]}
                        placeholder="Enter whole number"
                        onChange={(e) => {
                          setChangedBreachFields((prev) => ({ ...prev, [key]: true }));
                          setFormState((prev) => ({ ...prev, [key]: e.target.value }));
                        }}
                        error={errors[key]}
                      />
                    ))}
                </>
              )}
            </>
          )}
        </section>
      </StyledForm>
      {isEditMode && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem" }}>
          <CTAButton isSecondary onClick={handleCancel}>
            Cancel
          </CTAButton>
          <CTAButton isLoading={isLoading} onClick={handleSubmit}>
            Save
          </CTAButton>
        </div>
      )}
      {errors?.form && <ErrorModal data-testid="error" error={errors.form} hide={() => setErrors({})} />}
    </div>
  );
};
